import React, { useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import { robotoFonts, Typography } from '../../components/typography';
import { CompVoucher, selectEntitlementDetails } from '.';
import { useDeviceInfo } from '../../retail-config/device';
import { VoucherPrintItem } from './voucher-print-item';
import { ButtonPlaceholderShape, ListItem } from './styles';
import { ScrollableList, ScrollableListWrapper } from '../shared-list-styles';

type Props = {
  vouchers: CompVoucher[];
  primaryIdentifier: string;
  onVoucherUpdated: (voucher: CompVoucher) => void;
};

export default function Comps({ vouchers, primaryIdentifier, onVoucherUpdated }: Props) {
  const [printing, setPrinting] = useState(false);
  const entitlementDetails = useSelector(selectEntitlementDetails);

  const maxCompsPerDay = useDeviceInfo().features.comps?.maxCompsPerDay ?? 4;
  const vouchersWithPlaceholders = createVoucherSlotsWithPlaceholders({ vouchers, maxCompsPerDay });

  return (
    <Wrapper>
      <Heading>AVAILABLE VOUCHERS</Heading>
      <InfoPanel>Vouchers shown below are available until the end of the day.</InfoPanel>
      <ScrollableListWrapper>
        <ScrollableList>
          {vouchersWithPlaceholders.map((voucher: CompVoucher | null, i: number) => {
            const key = voucher?.uuid || `placeholder-${i + 1}`;
            return voucher ? (
              <VoucherPrintItem
                disabled={printing}
                entitlementDetails={entitlementDetails}
                key={key}
                onPrintingChange={setPrinting}
                onVoucherUpdated={onVoucherUpdated}
                primaryIdentifier={primaryIdentifier}
                voucher={voucher}
              />
            ) : (
              <VoucherPlaceholder index={i} key={key} />
            );
          })}
        </ScrollableList>
      </ScrollableListWrapper>
    </Wrapper>
  );
}

function VoucherPlaceholder({ index }: { index: number }) {
  return (
    <ListItem>
      #{index + 1}
      <NoVoucher>&nbsp;</NoVoucher>
    </ListItem>
  );
}

function createVoucherSlotsWithPlaceholders({
  vouchers,
  maxCompsPerDay
}: {
  vouchers: CompVoucher[];
  maxCompsPerDay: number;
}): (CompVoucher | null)[] {
  return Array.from({ length: maxCompsPerDay }).map((_, i) => vouchers[i] || null);
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 1.6rem;
  min-height: 0;
  position: relative;
`;

const InfoPanel = styled(Typography).attrs(() => ({ as: 'p', size: 'sm' }))`
  align-items: center;
  background-color: #e4f9ff;
  display: flex;
  justify-content: center;
  margin: 0;
  padding: 1.6rem 1.2rem;
`;

const Heading = styled.h1`
  font-size: ${robotoFonts.xl};
  margin: 0;
`;

const NoVoucher = styled(ButtonPlaceholderShape)`
  border-color: ${({ theme }) => theme.colors.neutral};
  border-style: dashed;
`;
